import React from 'react'

import styled from '@emotion/styled'
import {
  Box,
  Button,
  ButtonColorVariant,
  Color,
  Display,
  GridDisplay,
  Heading2,
  JustifyContent,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { CopyConfirmation } from 'SocialLinks/CopyConfirmation'
import { SocialIconShareButtons } from 'SocialLinks/SocialShareButtons'
import { SOCIAL_TYPES } from 'SocialLinks/socialReferralHelper'
import { CopyToClipboard } from 'react-copy-to-clipboard'

import { actionTypes } from 'actions/actionTypes'

const RAFLinkPromoInput = withCitrusTheme('input', (theme) => ({
  border: `2px solid ${theme.color(Color.ColdGrey_100)}`,
  padding: `12px`,
  marginRight: `10px`,
  flex: `1`,
  borderRadius: `3px`,
}))

const RAFLinkShareContainer = styled.div`
  width: 100%;

  @media (max-width: 767px) {
    display: none;
  }
`

// SocialIconShareButtons only applies its desktop divider/spacing at min-width: 1025px
// (see SocialIconShareButtons.css .pageSocialButtons), one pixel past Citrus's own
// Large breakpoint (1024px). Matching that exact boundary here — rather than Citrus's
// responsive-array breakpoints — avoids a 1024px-wide gap where this grid has already
// switched to the two-column layout but the icons haven't gained their divider yet.
const RAFLinkShareGrid = styled(Box)`
  @media (min-width: 1025px) {
    grid-template-columns: 1fr auto;
  }

  &[data-has-banner='true'] {
    @media (min-width: 1025px) {
      grid-template-areas: 'input icons' 'banner banner';
    }
  }

  &[data-has-banner='false'] {
    @media (min-width: 1025px) {
      grid-template-areas: 'input icons';
    }
  }
`

const RAFLinkShareIconsBox = styled(Box)`
  align-self: center;

  /* SocialIconShareButtons.css always applies a 20px top margin below 1025px
     (.socialButtons); without this override it stacks on top of the rowGap
     here, doubling the intended spacing below the banner/input. */
  & > section {
    margin-top: 0;
  }

  @media (min-width: 1025px) {
    align-self: stretch;
  }
`

interface Props {
  userRAFLink: string
  userFirstName: string
  trackingReferFriendSocialSharing: () => void
  device: string
  trackUserFreeFoodLinkShare: ({ target }: { target: string }) => void
  trackingReferFriend: (actionType: string, actionName: string) => void
  isBoost: boolean
  credit: string
}

export const RAFLinkShare = ({
  userRAFLink,
  userFirstName,
  trackingReferFriendSocialSharing,
  device,
  trackUserFreeFoodLinkShare,
  trackingReferFriend,
  isBoost,
  credit,
}: Props) => {
  const [copied, setCopied] = React.useState<boolean>(false)
  const onCopy = () => {
    setCopied(true)
    trackingReferFriend(actionTypes.REFER_FRIEND_LINK_COPIED, 'ReferFriendLink Copied')
    trackUserFreeFoodLinkShare({ target: SOCIAL_TYPES.link })
  }

  return (
    <RAFLinkShareContainer>
      <Box paddingBottom={5} display={Display.Flex} justifyContent={JustifyContent.Center}>
        <Heading2 color={isBoost ? Color.White : undefined}>Share your referral code</Heading2>
      </Box>
      <RAFLinkShareGrid
        width="100%"
        display={GridDisplay.Grid}
        gridTemplateColumns="1fr"
        gridTemplateAreas={copied ? '"input" "banner" "icons"' : '"input" "icons"'}
        rowGap={6}
        columnGap={0}
        paddingBottom={2}
        data-has-banner={copied ? 'true' : 'false'}
      >
        <Box display={Display.Flex} height="48px" gridArea="input">
          <RAFLinkPromoInput type="text" value={userRAFLink} readOnly />
          <CopyToClipboard text={userRAFLink} onCopy={() => onCopy()}>
            <Button colorVariant={ButtonColorVariant.Primary} data-testid="rafLinkCopyBtn">
              {copied ? 'Copy again' : 'Copy link'}
            </Button>
          </CopyToClipboard>
        </Box>
        <RAFLinkShareIconsBox
          height="48px"
          display={Display.Flex}
          gridArea="icons"
          justifyContent={JustifyContent.Center}
        >
          <SocialIconShareButtons
            referralCode={userRAFLink}
            userFirstName={userFirstName}
            trackingReferFriendSocialSharing={trackingReferFriendSocialSharing}
            device={device}
            trackUserFreeFoodLinkShare={trackUserFreeFoodLinkShare}
          />
        </RAFLinkShareIconsBox>
        {copied && (
          <Box gridArea="banner">
            <CopyConfirmation credit={credit} isBoost={isBoost} />
          </Box>
        )}
      </RAFLinkShareGrid>
    </RAFLinkShareContainer>
  )
}
